/**
 * VybeTraderTerminalDslEngine — main thread, async, registry-driven.
 *
 * The engine is intentionally tiny: it parses a single line, looks up
 * the resolved dotted path in the shared `DslRegistry`, runs the
 * handler, and returns a typed `DslResult`. All DSL function bodies
 * live in `engine/modules/*` and self-register via import side effects.
 */

import { UnknownFunctionError, toErrorPayload } from "./errors";
import { parse } from "./parser";
import { dslRegistry, type DslRegistry } from "./registry";
import type { DslListedFunction, DslResult } from "./types";

/** Map shell-style shortcuts to valid DSL before parsing. */
function normalizeTerminalInput(trimmed: string): string {
  const token = trimmed.toLowerCase();
  if (token === "help" || token === "?") {
    return "Help()";
  }
  return trimmed;
}

export class VybeTraderTerminalDslEngine {
  private modulesReady: Promise<void> | null = null;

  constructor(
    private readonly registry: DslRegistry = dslRegistry,
    // Injectable for tests; importing the modules barrel triggers each
    // module's `dslRegistry.register(...)` side effects exactly once.
    private readonly loadModules: () => Promise<unknown> = () =>
      import("./modules")
  ) {
    this.ensureModulesReady().catch(() => {
      // Swallow here; execute() surfaces (and retries) the failure.
    });
  }

  /**
   * Kick off (or reuse) the modules import. On failure the cached
   * promise is cleared so the next call retries instead of leaving the
   * terminal permanently broken, and the error is wrapped in a clear
   * message.
   */
  private ensureModulesReady(): Promise<void> {
    if (!this.modulesReady) {
      this.modulesReady = Promise.resolve()
        .then(() => this.loadModules())
        .then(() => undefined)
        .catch((cause) => {
          this.modulesReady = null;
          throw new Error(
            `Terminal engine failed to load its command modules; it will retry on the next command. (${
              cause instanceof Error ? cause.message : String(cause)
            })`,
            { cause }
          );
        });
    }
    return this.modulesReady;
  }

  /**
   * Parse and run a single DSL expression. The promise always resolves
   * with a `DslResult`; errors are converted to a structured payload
   * rather than rejecting, so the UI can render them uniformly.
   */
  async execute(input: string): Promise<DslResult> {
    const startedAt = performance.now();
    try {
      await this.ensureModulesReady();
      // Yield once before any synchronous work so a long input never
      // delays paint/input handling on the main thread.
      await Promise.resolve();

      const trimmed = input.trim();
      if (!trimmed) {
        return {
          status: "error",
          error: { code: "PARSE_ERROR", message: "Empty input", offset: 0 },
          durationMs: performance.now() - startedAt,
        };
      }

      const call = parse(normalizeTerminalInput(trimmed));
      const key = call.path.join(".");
      const entry = this.registry.resolve(key);
      if (!entry) {
        throw new UnknownFunctionError(key);
      }

      const value = await entry.fn(call.args, { startedAt });
      return { status: "ok", value, durationMs: performance.now() - startedAt };
    } catch (err) {
      return {
        status: "error",
        error: toErrorPayload(err),
        durationMs: performance.now() - startedAt,
      };
    }
  }

  /** Snapshot of currently registered functions (sorted by path). */
  list(): DslListedFunction[] {
    return this.registry.list();
  }
}

/**
 * Singleton engine for the whole client. Modules are loaded lazily on
 * construction; the first `execute` call awaits that import.
 */
export const vybeTraderTerminalDslEngine = new VybeTraderTerminalDslEngine();
